import { CacheService } from 'src/app/shared/services/cache.service';
import { Component, Inject, OnInit } from '@angular/core';
import { User } from '../../../../core/models/user.model';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { OauthProvider } from '../../../../oauth/providers';
import { RequestBodyService } from '../../../services/request-body.service';
import { CommonUtilService } from '../../../services/CommonUtil.service';
import { OutreachService } from '../../../services/outreach.service';
import { ReConnectSettingComponent } from '../../re-connect-setting/re-connect-setting.component';
import { AccountAttributesEnum } from '../../../enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../confirmation-dialog';
import { take } from 'rxjs/operators';
import { OutreachApiService } from '../../../services/api';
import { LoadingService } from '../../../services';
import { Observable } from 'rxjs';
import { getErrorMessage } from 'src/app/shared/helpers';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IndividualAssignmentComponent } from '../individual-assignment/individual-assignment.component';
import { SiModalModule } from '../../si-modal';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { FilterPipeModule, OrderByPipeModule } from '../../../pipes';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  DuplicateResolutionRadioGroupModule
} from '../../ui-components/duplicate-resolution-group/duplicate-resolution-radio-group.module';
import { MatButtonModule } from '@angular/material/button';

const LOADING_TAG: string = 'outreach-export-loading-tag';

@UntilDestroy()
@Component({
    selector: 'app-outreach',
    templateUrl: './outreach.component.html',
    standalone: true,
    imports: [
      CommonModule, SiModalModule,
      MatFormFieldModule, FormsModule, MatRadioModule,
      FontAwesomeModule, FilterPipeModule, OrderByPipeModule, MatInputModule,
      DuplicateResolutionRadioGroupModule, IndividualAssignmentComponent, MatButtonModule, MatInputModule
    ],
})
export class OutreachComponent implements OnInit {
    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG);

    pages = {
        tags: 0,
        sequence: 1,
        leadOwner: 2,
        mailbox: 3,
        duplicates: 4,
        export: 5,
        exportFinished: 6,
        exportEmpty: 7,
        exportFailed: 8,
        requiresReauth: 9,
        exportError: 10
    };
    contacts = [];
    isBusy = true;
    currentPage = this.pages.tags;
    busyLabel = 'Loading ...';
    initialPage = this.pages.tags;
    busySublabel = '';
    showPreviousButton = false;
    showNextButton = true;
    exportType = 'outreach';
    duplicateMatchIds = [];
    duplicateCount = 0;
    exportedContactCount = 0;
    warnings = [];
    isShowingWarnings = false;
    tags = '';
    sequencesLoaded = false;
    contactOwnersLoaded: boolean = false;
    selectedSequence: Set<number> = new Set<number>();
    sequences = [];
    sequenceFilter = '';
    selectedContactOwner: number;
    id: number;
    mailboxesLoaded = false;
    showCloseButton = false;
    selectedMailbox = '';
    mailboxes = [];
    mailboxFilter = '';
    duplicateResolution = 'update';
    showCancelButton = true;
    showReconnectButton = false;
    exportError = '';
    settings: any;
    showRetryButton = false;
    isButtonDisable = false;
    isOutReachComponentOpened: boolean = true;
    individualFlow: boolean;
    individualIds = [];
    accuracyTier: string;
    accuracyTierText: string;
    filterLeadOwner: string = '';
    leadOwners;
    private cachedSequences$: Observable<any>;
    private cachedMailboxes$: Observable<any>;
    readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;

    constructor(
        private commonUtilService: CommonUtilService,
        private outreachService: OutreachService,
        private authenticationService: AuthenticationService,
        private requestBodyService: RequestBodyService,
        private outreachApiService: OutreachApiService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<OutreachComponent>,
        private readonly loadingService: LoadingService,
        private cacheService: CacheService,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        if (this.data['contacts']) {
            this.contacts = this.data['contacts'];
            this.accuracyTier = this.data['accuracyTier'];
            this.accuracyTierText = this.data['accuracyTierText'];
        }
    }
    ngOnInit() {
        this.setCachedSequences();
        this.setCachedMailboxes();

        this.individualFlow = this.outreachService.isIndividualAssignment;
        this.authenticationService.currentUser$.pipe(untilDestroyed(this)).subscribe((u: User) => {
            this.settings = u.user_meta;
            this.id = u.id;
        });
        this.initializeFirstStep();
    }

    setCachedSequences() {
        this.cacheService.delete('outreach-export-sequences');
        this.cachedSequences$ = this.cacheService.getCachedObservable(
            'outreach-export-sequences',
            this.loadingService.loadingWrapper(this.outreachApiService.getSequences(), LOADING_TAG),
        );
    }

    setCachedMailboxes() {
        this.cacheService.delete('outreach-export-mailboxes');
        this.cachedMailboxes$ = this.cacheService.getCachedObservable(
            'outreach-export-mailboxes',
            this.loadingService.loadingWrapper(this.outreachApiService.getMailBoxes(), LOADING_TAG),
        );
    }

    errorRefreshToken() {
        if (this.isOutReachComponentOpened) {
            this.onClose();
            const modalConfig: MatDialogConfig = {
                panelClass: 'modal-lg',
                data: {}
            };
            if (this.data['contacts']) {
                modalConfig.data['provider'] = OauthProvider.Outreach;
                modalConfig.data['match_ids'] = this.contacts;
            }
            this.matDialog.open(ReConnectSettingComponent, modalConfig);
        }
    }

    validateLoading() {
        if (this.mailboxesLoaded && this.sequencesLoaded && this.contactOwnersLoaded) {
            this.isBusy = false;
        }
    }

    selectMailbox = function (mid) {
        if (this.selectedMailbox === mid) {
            this.selectedMailbox = '';
            // this.isButtonDisable = true; // 4255
            this.executeCurrentStep();
        } else {
            this.selectedMailbox = mid;
            // this.isButtonDisable = false;
            this.executeCurrentStep();
        }
    };

    isMailboxSelected = function (mid) {
        return (this.selectedMailbox === mid);
    };

    selectSequence(lid) {
        if (this.selectedSequence.has(lid)) {
            this.selectedSequence.delete(lid);
        } else {
            this.selectedSequence.add(lid);
        }
    }

    rowSelected(cid) {
        return this.selectedSequence.has(cid);
    }

    executeCurrentStep() {
        if (this.currentPage === this.initialPage) {
            this.showNextButton = true;
            this.showPreviousButton = false;
            this.isButtonDisable = false;
        } else {
            this.showPreviousButton = true;
            this.showNextButton = true;
            this.isBusy = false;
            this.isButtonDisable = false;
        }

        if (this.currentPage === this.pages.sequence) {
            this.loadSequences();
            this.loadMailBoxes();
        }
        if (this.currentPage === this.pages.mailbox) {
            // this.isButtonDisable = true;
            if (this.selectedSequence && this.selectedSequence.size === 0) {
                this.isButtonDisable = false;
            } else if (this.selectedSequence && this.selectedSequence.size !== 0 && this.selectedMailbox !== '') { // 4223
                this.isButtonDisable = false;
            } else {
                this.isButtonDisable = true;
            }
        }
        if (this.currentPage === this.pages.duplicates) {
            this.showCloseButton = false;
            this.isButtonDisable = false;
            this.showPreviousButton = true;
            this.isButtonDisable = false;

            if (this.settings.orPromptForTags === '0' && this.settings.orPromptForSequence === '0') { // 4261
                this.showPreviousButton = false;
            }

            this.validateDuplicates();
        }
        if (this.currentPage === this.pages.export) {
            this.showPreviousButton = false;
            this.export();
        }
        if (this.currentPage === this.pages.exportFinished) {
            this.showCancelButton = false;
            this.showCloseButton = true;
            this.isButtonDisable = false;
            if (this.authenticationService.hasBypassClearSelectionAfterExport() === false) {
                this.contacts = [];
            }
        }
        if (this.currentPage === this.pages.exportEmpty) {
            this.showNextButton = false;
            this.showCloseButton = true;
            this.isButtonDisable = false;
        }
        if (this.currentPage === this.pages.requiresReauth) {
            this.showCancelButton = true;
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.showReconnectButton = true;
            this.showCloseButton = false;
            this.isButtonDisable = false;
        }
    }

    onClose() {
        this.isOutReachComponentOpened = false;
        this.matDialogRef.close();
        this.outreachService.isIndividualAssignment = false;
    }
    initializeFirstStep() {
        this.isBusy = true;
        this.currentPage = this.pages.tags;
        this.initialPage = this.currentPage;
        if (this.settings.orPromptForTags === '0' && this.currentPage === this.pages.tags) {
            this.currentPage = this.currentPage + 1;
        }
        if (this.settings.orPromptForSequence === '0' && this.currentPage === this.pages.sequence) {
            this.currentPage = this.currentPage + 1;
        }
        if (this.settings.orPromptForLeadOwners !== '1' && this.currentPage === this.pages.leadOwner) {
            this.currentPage = this.currentPage + 1;
        }
        if (this.settings.orPromptForSequence === '0' && this.currentPage === this.pages.mailbox) {
            this.currentPage = this.currentPage + 1;
        }

        this.initialPage = this.currentPage;
        this.processAccount();
        this.loadSequences();
        this.loadContactOwners();
        this.loadMailBoxes();
        this.executeCurrentStep();
    }

    loadContactOwners() {
        this.outreachApiService.getContactOwners(this.id).pipe(untilDestroyed(this)).subscribe((response) => {
            this.contactOwnersLoaded = true;
            this.leadOwners = response;
            if (this.settings.orDefaultOwnerIsConnectedUser === '1') {
                this.selectedContactOwner = response.filter(item => item.email === this.settings.orEmail)?.[0]?.id;
            } else if (this.settings.orPromptForLeadOwners === '1') {
                this.selectedContactOwner = null;
            }
        }, error => {
            const errorCode = error.error.error_code;
            if (errorCode === 'invalid_grant' ||
                errorCode === 'UNAUTHORIZED' ||
                errorCode === 'BAD REQUEST') { // 4097
                this.outreachApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                    this.loadContactOwners();
                }, () => {
                    this.errorRefreshToken();
                });
            } else {
                this.isBusy = false;
                this.currentPage = this.pages.exportError;
                this.showNextButton = false;
                this.exportError = error.error.error_message;
            }
        }, () => {
            this.contactOwnersLoaded = true;
            this.validateLoading();
        });
    }

    processAccount() {
        const matchIds = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        this.loadingService.loadingWrapper(this.outreachApiService.processAccounts(matchIds), LOADING_TAG).pipe(untilDestroyed(this)).subscribe();
    }

    loadSequences(): any {
        this.cachedSequences$.pipe(take(1), untilDestroyed(this)).subscribe((response) => {
            this.sequences = response;
            this.sequencesLoaded = true;
        }, error => {
            const errorCode = error.error.error_code;
            if (errorCode === 'invalid_grant' ||
                errorCode === 'UNAUTHORIZED' ||
                errorCode === 'BAD REQUEST') { // 4097
                this.outreachApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(response => {
                    this.setCachedSequences();
                    this.processAccount();
                    this.loadSequences();
                }, refreshError => {
                    this.errorRefreshToken();
                });
            } else {
                this.isBusy = false;
                this.currentPage = this.pages.exportError;
                this.showNextButton = false;
                this.exportError = error.error.error_message;
            }
        }, () => {
            this.sequencesLoaded = true;
            this.validateLoading();
        });
    }

    loadMailBoxes() {
        this.cachedMailboxes$.pipe(take(1), untilDestroyed(this)).subscribe((response) => {
            this.mailboxes = response;
            this.mailboxesLoaded = true;
            this.isBusy = false;
        }, error => {
                this.outreachApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(response => {
                    this.setCachedMailboxes();
                    this.loadMailBoxes();
                    this.mailboxesLoaded = true;
                    this.validateLoading();
                }, refreshError => {
                    this.currentPage = this.pages.requiresReauth;
                    this.mailboxesLoaded = true;
                    this.validateLoading();
                });
        }, () => {
            this.sequencesLoaded = true;
            this.validateLoading();
        });
    }
    retryExport() {
        this.currentPage = this.pages.export;
        this.showRetryButton = false;
        this.executeCurrentStep();
    }

    next() {
        const currentIndex = this.currentPage;
        let nextIndex = currentIndex + 1;
        this.currentPage++;
        if (this.settings.orPromptForTags === '0' && nextIndex === this.pages.tags) {
            nextIndex++;
        }
        if (this.settings.orPromptForSequence === '0' && nextIndex === this.pages.sequence) {
            nextIndex++;
        }
        if (this.settings.orPromptForSequence === '0' && nextIndex === this.pages.mailbox) {
            nextIndex++;
        }
        if (this.settings.orPromptForLeadOwners !== '1' && nextIndex === this.pages.leadOwner) {
            nextIndex++;
        }
        this.currentPage = nextIndex;
        this.executeCurrentStep();
    }


    previous() {
        let page = this.currentPage;
        let previousIndex = page - 1;
        page--;
        if (this.currentPage === this.pages.exportEmpty) {
            page = this.pages.duplicates;
        }
        if (this.settings.orPromptForTags === '0' && this.currentPage === this.pages.tags) {
            page--;
        }

        if (this.settings.orPromptForSequence === '0' && this.currentPage === this.pages.sequence) {
            page--;
        }

        if (this.settings.orPromptForSequence === '0' && this.currentPage === this.pages.mailbox) {
            page--;
        }

        if (this.settings.orPromptForSequence === '0' && this.currentPage === this.pages.duplicates) {  // 4261
            page = page - 2;
        }

        if (this.settings.orPromptForLeadOwners !== '1' && page === this.pages.leadOwner) {
            page--;
        }

        if (previousIndex === this.pages.sequence) {
            this.individualFlow = this.outreachService.isIndividualAssignment;
        }

        this.currentPage = page;
        this.executeCurrentStep();
    }
    validateDuplicates() {
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        const matchIds = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        this.loadingService.loadingWrapper(this.outreachApiService.validateDuplicates(matchIds), LOADING_TAG).pipe(untilDestroyed(this)).subscribe((response: any) => {
            const dupeIds = [];
            let dupesCount = 0;
            response.contacts.forEach((item) => {
                if (item['duplicate'] === true) {
                    dupeIds.push(item);
                    dupesCount++;
                }
            });
            this.duplicateMatchIds = dupeIds;
            this.duplicateCount = dupesCount;
            if (dupesCount === 0) {
                this.next();
            } else {
                this.isBusy = false;
            }
        });
    }

    export() {
        this.isShowingWarnings = false;
        this.warnings = [];
        // this.busyLabel = 'Exporting ...';
        this.busyLabel = '';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        let tagArray = [];
        const toBeExported = [];
        this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return toBeExported.push({ id: contact.matchId });
            } else {
                return contact;
            }
        });
        if (this.duplicateResolution === 'skip') {
            for (const key in this.duplicateMatchIds) {
                if (this.duplicateMatchIds.hasOwnProperty(key)) {
                    this.commonUtilService.removeMatchId(toBeExported, this.duplicateMatchIds[key]);
                    this.commonUtilService.removeMatchId(this.individualIds, this.duplicateMatchIds[key]);
                }
            }
        }
        if (toBeExported.length === 0) {
            this.currentPage = this.pages.exportEmpty;
            this.isBusy = false;
            this.showNextButton = false;
            this.executeCurrentStep();
            return;
        }
        this.exportedContactCount = toBeExported.length;
        if (this.tags.length > 0) {
            tagArray = this.tags.split(',');
        }

        if (this.individualIds && this.individualIds.length > 0 && this.outreachService.isIndividualAssignment === true) {
            this.outreachApiService.exportV2(tagArray, this.selectedMailbox, this.duplicateResolution, this.individualIds, this.selectedContactOwner)
            .pipe(untilDestroyed(this))
            .subscribe((response: any) => {
                this.warnings = response;
                this.isBusy = false;
                this.next();
                this.requestBodyService.changeRevealMatchesID({ 'type': 'outreach', 'data': this.individualIds });
                this.showPreviousButton = false;
                this.showNextButton = false;
            }, (error) => {
                const errMsg = getErrorMessage(error, true);
                if (errMsg) {
                    this.exportError = errMsg;
                    this.showRetryButton = true;
                } else {
                    this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
                }
                this.currentPage = this.pages.exportFailed;
                this.showPreviousButton = false;
                this.showNextButton = false;
                this.isBusy = false;
            });
        } else {
            this.loadingService.loadingWrapper(this.outreachApiService.exportV2Batch(
                tagArray,
                this.selectedMailbox,
                Array.from(this.selectedSequence),
                this.duplicateResolution,
                toBeExported,
                this.selectedContactOwner
            ), LOADING_TAG).pipe(untilDestroyed(this)).subscribe((response: any) => {
                    this.warnings = response;
                    this.isBusy = false;
                    this.next();
                    this.requestBodyService.changeRevealMatchesID({ 'type': 'outreach', 'data': toBeExported });
                    this.showPreviousButton = false;
                    this.showNextButton = false;
                }, (error) => {
                    const errMsg = getErrorMessage(error, true);
                    if (errMsg) {
                        this.exportError = errMsg;
                        this.showRetryButton = true;
                    } else {
                        this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
                    }
                    this.currentPage = this.pages.exportFailed;
                    this.showPreviousButton = false;
                    this.showNextButton = false;
                    this.isBusy = false;
                });
        }

    }
    showWarnings() {
        this.isShowingWarnings = !this.isShowingWarnings;
    }

    toggleFlow($event, val) {
        let filterSequence = this.individualIds.filter(item => item['sequence']).length > 0;
        if (val === false && filterSequence == true) {
            $event.preventDefault();
            const deleteModalData: MatDialogConfig<ConfirmationDialogData> = {
                panelClass: 'modal-md',
                data: {
                    title: 'Confirmation',
                    confirmMessage: 'Changing the flow back to the basic assignment will clear the mapping between sequence and contacts. Are you sure you want to proceed?',
                    isShowButton: true,
                },
            };
            this.matDialog.open(ConfirmationDialogComponent, deleteModalData).afterClosed().pipe(take(1)).subscribe((success: boolean) => {
                if (success) {
                    this.contacts.filter(item => {
                        if (item['selectedSequence']) {
                            delete item['selectedSequence'];
                        }
                    });
                    this.individualIds.filter(item => {
                        if (item['sequence']) {
                            delete item['sequence'];
                        }
                    });
                    this.outreachService.isIndividualAssignment = val;
                    this.individualFlow = val;
                } else {
                    this.outreachService.isIndividualAssignment = true;
                    this.individualFlow = true;
                }
            });
        } else {
            this.outreachService.isIndividualAssignment = val;
            this.individualFlow = val;
            this.selectedSequence.clear();
        }
    }

    onIndividual(event) {
        this.individualIds = event;
    }

    selectLeadOwner(id) {
        this.selectedContactOwner = id;
    }
}
